// React
import React, { Fragment, useMemo } from 'react'
import { compose } from 'redux'

// Components
import BDatePicker, { HostStyles } from './styles-next'

// Libraries
import { faCalendar as farCalendar } from '@fortawesome/pro-regular-svg-icons'

// Hooks
import { useOrm, shallowEqual } from 'shared/utils/useOrm'
import { useOpeningHours } from 'shared/utils/useOpeningHours'
import { useSettings } from 'shared/utils/useSettings'
import { useTranslation } from 'shared/utils/useTranslation'
import { useDatePicker } from 'store/hooks/useDatePicker'
import { useSelector } from 'react-redux'

// Shared
import withStore from 'shop-components/utils/withStore'
import withTheme from 'shop-components/utils/withTheme'
import { Format as FormatUtils } from 'back_office/v2/utils/utils'
import { isLocationInputEnabled, isDaySelectable, isTimeSelectable } from 'ui/components/Picker/utils'

const getLabelKey = (pricingEnabled, availabilityEnabled) => {
  if (pricingEnabled && availabilityEnabled) {
    return 'user.store.view_prices_and_availability'
  }

  if (pricingEnabled && !availabilityEnabled) {
    return 'user.store.view_prices'
  }

  if (!pricingEnabled && availabilityEnabled) {
    return 'user.store.view_availability'
  }
}

const ButtonNext = () => {
  const openingHours = useOpeningHours()

  const cartId = useSelector((state) => state.client.cartId)

  const cart = useOrm('carts', (carts) => {
    return carts.getById(cartId || window.booqableOptions.cart?.id)
  })
  const locations = useOrm('locations', (locations) => {
    return {
      count: locations.size(),
      start: locations.getById(cart?.start_location_id ?? window.booqableOptions.cart?.startLocationId),
      stop: locations.getById(cart?.stop_location_id ?? window.booqableOptions.cart?.stopLocationId)
    }
  }, shallowEqual)

  const useTimes = useSettings('store.use_times')
  const useAMOrPM = useSettings('dates.use_am_pm')
  const isPricingEnabled = useSettings('pricing.enabled')
  const isAvailabilityEnabled = useSettings('store.use_availability')

  const { open } = useDatePicker()

  const dates = {
    start: cart?.starts_at ?? window.booqableOptions.cart?.from,
    stop: cart?.stops_at ?? window.booqableOptions.cart?.till
  }

  const isDelivery = cart?.data?.fulfillment_type === 'delivery'

  const isStartValid = useMemo(
    () =>
      isDaySelectable({
        type: 'start',
        openingHours,
        date: dates.start,
        values: {
          starts_at: dates.start,
          stops_at: dates.stop
        }
      }) &&
      isTimeSelectable({
        type: 'start',
        openingHours,
        date: dates.start,
        values: {
          starts_at: dates.start,
          stops_at: dates.stop
        }
      }),
    [dates.start, dates.stop, openingHours]
  )

  const isStopValid = useMemo(
    () =>
      isDaySelectable({
        type: 'stop',
        openingHours,
        date: dates.stop,
        values: {
          starts_at: dates.start,
          stops_at: dates.stop
        }
      }) &&
      isTimeSelectable({
        type: 'stop',
        openingHours,
        date: dates.stop,
        values: {
          starts_at: dates.start,
          stops_at: dates.stop
        }
      }),
    [dates.start, dates.stop, openingHours]
  )

  const { t } = useTranslation('user')

  const hasLocations = locations.count > 1 && (isLocationInputEnabled('start') || isLocationInputEnabled('stop'))
  const hasData = hasLocations ?
    !!dates.start && !!dates.stop && !!locations.start && !!locations.stop :
    !!dates.start && !!dates.stop

  let timeFormat = ''

  if (useTimes) {
    timeFormat = useAMOrPM ? ' hh:mm A' : ' HH:mm'
  }

  const labelKey = getLabelKey(isPricingEnabled, isAvailabilityEnabled)

  if (!cart) return null

  if (!hasData) {
    return (
      <Fragment>
        <HostStyles />
        <BDatePicker>
          <BDatePicker.Wrapper onClick={open}>
            <BDatePicker.CalendarIcon icon={farCalendar} />
            <BDatePicker.Text>
              {t('user.store.select_a_period')}
              {labelKey && <BDatePicker.ExpandedTitle>; {t(labelKey)}</BDatePicker.ExpandedTitle>}
            </BDatePicker.Text>
          </BDatePicker.Wrapper>
        </BDatePicker>
      </Fragment>
    )
  }

  return (
    <Fragment>
      <HostStyles />
      <BDatePicker>
        <BDatePicker.WrapperWithData onClick={open}>
          {hasLocations && !isDelivery && (
            <BDatePicker.Location>
              {locations.start?.name ?? t('user.store.start_location')}
            </BDatePicker.Location>
          )}
          <BDatePicker.Dates isValid={isStartValid && isStopValid}>
            {dates.start && (
              <Fragment>
                {FormatUtils.date(dates.start, 'MMM D[,] YYYY')}
                {useTimes && FormatUtils.date(dates.start, timeFormat)}
              </Fragment>
            )}
            &nbsp;-&nbsp;
            {dates.stop && (
              <Fragment>
                {FormatUtils.date(dates.stop, 'MMM D[,] YYYY')}
                {useTimes && FormatUtils.date(dates.stop, timeFormat)}
              </Fragment>
            )}
          </BDatePicker.Dates>
        </BDatePicker.WrapperWithData>
      </BDatePicker>
    </Fragment>
  )
}

const decoratedComponent = compose(
  withStore,
  withTheme
)(ButtonNext)

export default decoratedComponent
